import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import classNames from 'classnames';
import { Icon, Input } from 'Components';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import {
	ASSIGN_USERS_OPTION,
	options
} from 'LearningManager/constants/createAssignment.constant';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import AssignmentUsersPicker from './AssignmentUsersPicker';
import styles from './AssignmentActionFlowModal.module.scss';

export interface AudienceSelection {
	methodId: number | null;
	entityIds: number[];
	userIds: string[];
}

export const EMPTY_AUDIENCE: AudienceSelection = {
	methodId: null,
	entityIds: [],
	userIds: []
};

export const hasAudienceSelection = (value: AudienceSelection): boolean =>
	value.methodId === ASSIGN_USERS_OPTION.INDIVIDUAL
		? value.userIds.length > 0
		: value.entityIds.length > 0;

// Roles are offered by the design, but the directory only filters users by
// group, so a role cannot be turned into the user_ids this action sends.
const UNSUPPORTED_METHOD_IDS: number[] = [ASSIGN_USERS_OPTION.ROLE];

const ENTITY_NOUN: Record<number, string> = {
	[ASSIGN_USERS_OPTION.ORGANIZATION]: 'organizations',
	[ASSIGN_USERS_OPTION.PROPERTY]: 'properties',
	[ASSIGN_USERS_OPTION.GROUP]: 'groups'
};

interface Props {
	assignmentId: number;
	scopes: string[];
	tenantId?: number;
	value: AudienceSelection;
	onChange: (value: AudienceSelection) => void;
}

const AssignmentAudiencePicker: React.FC<Props> = observer(
	({ assignmentId, scopes, tenantId, value, onChange }) => {
		const { assignUsersStore } = useStores();
		const {
			assignmentOrganizations,
			properties,
			groups,
			status
		} = assignUsersStore;

		const [search, setSearch] = useState('');
		const [groupPropertyIds, setGroupPropertyIds] = useState<number[]>([]);

		const { methodId } = value;

		useEffect(() => {
			if (methodId === ASSIGN_USERS_OPTION.ORGANIZATION) {
				assignUsersStore
					.fetchAssignmentOrganizations(tenantId)
					.catch(() => undefined);
			}
			if (
				methodId === ASSIGN_USERS_OPTION.PROPERTY ||
				methodId === ASSIGN_USERS_OPTION.GROUP
			) {
				assignUsersStore.fetchProperties(tenantId).catch(() => undefined);
			}
		}, [methodId, tenantId]);

		useEffect(() => {
			if (methodId !== ASSIGN_USERS_OPTION.GROUP) return;
			assignUsersStore
				.fetchGroupsByProperty(groupPropertyIds)
				.catch(() => undefined);
		}, [methodId, groupPropertyIds]);

		const selectMethod = (nextMethodId: number) => {
			if (UNSUPPORTED_METHOD_IDS.includes(nextMethodId)) return;
			setSearch('');
			setGroupPropertyIds([]);
			onChange({ methodId: nextMethodId, entityIds: [], userIds: [] });
		};

		const toggleEntity = (id: number | string) => {
			const numId = Number(id);
			const entityIds = value.entityIds.includes(numId)
				? value.entityIds.filter((entityId) => entityId !== numId)
				: [...value.entityIds, numId];
			onChange({ ...value, entityIds });
		};

		const toggleUser = (userId: string) => {
			const userIds = value.userIds.includes(userId)
				? value.userIds.filter((id) => id !== userId)
				: [...value.userIds, userId];
			onChange({ ...value, userIds });
		};

		const entities =
			methodId === ASSIGN_USERS_OPTION.ORGANIZATION
				? assignmentOrganizations
				: methodId === ASSIGN_USERS_OPTION.PROPERTY
				? properties
				: methodId === ASSIGN_USERS_OPTION.GROUP
				? groups
				: [];

		const filtered = search.trim()
			? entities.filter((entity) =>
					entity.name.toLowerCase().includes(search.toLowerCase())
			  )
			: entities;

		const isAllSelected =
			filtered.length > 0 &&
			filtered.every((entity) => value.entityIds.includes(entity.id));

		const handleSelectAll = () => {
			onChange({
				...value,
				entityIds: isAllSelected
					? []
					: filtered.map((entity) => entity.id)
			});
		};

		const isLoadingEntities =
			status.fetchAssignmentOrganizations === Status.Pending ||
			status.fetchProperties === Status.Pending ||
			status.fetchGroups === Status.Pending;

		const renderEntityList = () => (
			<>
				<div className={styles.pickerFilters}>
					{methodId === ASSIGN_USERS_OPTION.GROUP && (
						<MultiSelectDropdown
							placeholder="Filter by property"
							options={properties.map((property) => ({
								id: property.id,
								label: property.name
							}))}
							defaultSelectedIds={groupPropertyIds}
							onChange={setGroupPropertyIds}
						/>
					)}
					<Input.Search
						id="lms-ca-action-entity-search"
						className={styles.pickerSearch}
						placeholder="Search"
						suffix={<Icon type="search" role="img" />}
						allowClear
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>

				<div className={styles.usersHeader}>
					Select {ENTITY_NOUN[methodId as number] ?? 'items'}
					<span className={styles.required} aria-hidden="true">
						*
					</span>
					{filtered.length > 1 && (
						<span
							id="lms-ca-action-select-all"
							className={styles.selectAll}
							onClick={handleSelectAll}
						>
							{isAllSelected ? 'Deselect all' : 'Select all'}
						</span>
					)}
				</div>

				{isLoadingEntities ? (
					<div className={styles.pickerLoading}>
						<Spin />
					</div>
				) : filtered.length === 0 ? (
					<div className={styles.pickerEmpty}>
						{methodId === ASSIGN_USERS_OPTION.GROUP &&
						!groupPropertyIds.length
							? 'Pick a property to see its groups.'
							: 'Nothing to show here.'}
					</div>
				) : (
					<div className={styles.specificSubContent}>
						{filtered.map((entity) => (
							<CheckBoxCard
								key={entity.id}
								id={entity.id}
								htmlId={`lms-ca-action-entity-${entity.id}`}
								icon={<Icon type="propOptIcon" />}
								title={entity.name}
								desc={entity.label}
								isChecked={value.entityIds.includes(entity.id)}
								onChange={toggleEntity}
							/>
						))}
					</div>
				)}
			</>
		);

		return (
			<div id="lms-ca-assignment-audience-picker">
				<div className={styles.specificMethods}>
					{options.map((option) => {
						const isSelected = methodId === option.id;
						const isDisabled = UNSUPPORTED_METHOD_IDS.includes(
							option.id
						);
						return (
							<div
								key={option.id}
								id={`lms-ca-action-method-${option.id}`}
								className={classNames(styles.methodCard, {
									[styles['methodCard--selected']]: isSelected,
									[styles['methodCard--disabled']]: isDisabled
								})}
								onClick={() => selectMethod(option.id)}
							>
								<div className={styles.methodLabel}>
									{isSelected ? option.selectedIcon : option.icon}
									<span className={styles.methodHead}>
										{option.title}
									</span>
								</div>
								<div className={styles.methodValue}>
									{isDisabled
										? 'Not available for this action yet'
										: option.desc}
								</div>
							</div>
						);
					})}
				</div>

				{methodId === ASSIGN_USERS_OPTION.INDIVIDUAL ? (
					<AssignmentUsersPicker
						assignmentId={assignmentId}
						scopes={scopes}
						selectedUserIds={value.userIds}
						onToggle={toggleUser}
					/>
				) : methodId ? (
					renderEntityList()
				) : (
					<div className={styles.pickerEmpty}>
						Choose a selection method to continue.
					</div>
				)}
			</div>
		);
	}
);

export default AssignmentAudiencePicker;
